import { FormHelperText, Grid, Tooltip } from "@mui/material";
import FormField from "components/FormField/FormField";
import { Controller, useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import { useContext, useState, useEffect, useRef } from "react";
import productAttributesNames from "utils/products/productAttributes";
import StageProductContext from "./forms/StageProductContext";
import pluralMeasurementMaterial from "constants/pluralMeasurementMaterial";
import MDAlert from "components/MDAlert";
import { applicationRateMappings } from "modules/products/data/applicationRateMappings";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import ProposalContext from "./providers/ProposalContextProvider";
import { ProposalFormsContext } from "./providers/ProposalFormsContextProvider";
import { capitalCase } from "change-case";
import lengthEndAdornment from "constants/lenghtEndAdornment";
import { applicationRateLabelMappings } from "utils/product-types/getJobMeasurementMethodOptions";
import { requiresPlant } from "DDD/action-objects/ProductCreate";
import { ProductProductionRateType, ProductType } from "generated/graphql";
import { snakeCase } from "change-case";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import currencyEndAdornment from "constants/currencyEndAdornment";

function ProposalDefaultsAndInputs() {
  const { control, register, watch, getValues, setValue } =
    useFormContext<UpdateProposalProductInputSchema>();
  const { product, stage } = useContext(StageProductContext);
  const { proposal, inViewMode } = useContext(ProposalContext);
  const { organizationSettings, organizationSettingsLoading } = useContext(ProposalFormsContext);
  const materialAttributes = watch("material.attributes", {});
  const averageWorkingHoursPerDay = organizationSettings?.["average_working_hours_per_day"]
    ? +organizationSettings["average_working_hours_per_day"]
    : 0;
  const isSubcontracted = product.material.subcontracted;
  const materialNeeded = getValues("material.materialNeeded");

  // material needed can be updated from here or from the MaterialCosts component and we need to keep the two components in sync
  const [materialNeededInput, setMaterialNeededInput] = useState(materialNeeded || "");
  const plantProducts = watch("plantProducts");
  const plantProductsCount = (plantProducts ?? [])?.length;
  // Local state for statement of work to avoid triggering save on every keystroke
  const [localStatementOfWork, setLocalStatementOfWork] = useState(
    getValues("material.statementOfWork") ?? ""
  );
  const localStatementOfWorkRef = useRef(localStatementOfWork);
  localStatementOfWorkRef.current = localStatementOfWork;

  useEffect(() => {
    return () => {
      const currentFormValue = getValues("material.statementOfWork");
      if (localStatementOfWorkRef.current !== currentFormValue) {
        setValue("material.statementOfWork", localStatementOfWorkRef.current, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    };
  }, [getValues, setValue]);

  const handleMaterialNeededInputChange = (input) => {
    setMaterialNeededInput(input);
    setValue("material.materialNeeded", input, { shouldDirty: true });
  };
  const isUnitprice = product.material?.unitPriceProduct;

  const isConcreteWallOrColumn = [
    "ProductAttributeConcreteWall",
    "ProductAttributeConcreteColumn",
  ].includes(product.material?.attributes?.__typename);

  return (
    <Grid
      container
      spacing="16"
      sx={{
        justifyContent: { md: "space-between" },
      }}
    >
      <Grid className="table-form-field-inline-content">
        <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
          <Tooltip title="Profit Markup %">
            <FormField
              placeholder={product.material.markupPercentage}
              label="Profit Markup %"
              disabled={inViewMode}
              align="right"
              {...register("material.markup", { valueAsNumber: true })}
            />
          </Tooltip>
        </Grid>

        {Object.keys(materialAttributes).map(
          (
            attributeKey: keyof UpdateProposalProductInputSchema["material"]["attributes"],
            i: number
          ) => {
            if (
              attributeKey === "applicationRate" &&
              requiresPlant.map((pt) => snakeCase(pt)).includes(product.material.type)
            ) {
              return null;
            }

            return (
              <Grid key={i} data-attribute-key={attributeKey} sx={{ minWidth: "300px" }}>
                {product.material.type === snakeCase(ProductType.SLURRY_SEAL) ? (
                  <Grid className="table-form-field-inline-items">
                    <FormField
                      disabled={inViewMode}
                      align="right"
                      label={
                        applicationRateLabelMappings[product.material.organizationProductTypeName] +
                        capitalCase(product.material.measurementJob)
                      }
                      placeholder={product.material.attributes[attributeKey]}
                      {...register(`material.attributes.${attributeKey}`, {
                        valueAsNumber: productAttributesNames[attributeKey]?.valueAsNumber ?? false,
                      })}
                    />
                  </Grid>
                ) : (
                  <Grid className="table-form-field-inline-items">
                    <Tooltip
                      title={
                        isConcreteWallOrColumn && attributeKey === "height"
                          ? "Height in Feet"
                          : productAttributesNames[attributeKey]?.label ??
                            `Missing ${attributeKey} Product Attributes`
                      }
                    >
                      <FormField
                        disabled={inViewMode}
                        align="right"
                        label={
                          isConcreteWallOrColumn && attributeKey === "height"
                            ? "Height in Feet"
                            : productAttributesNames[attributeKey]?.label ??
                              `Missing ${attributeKey} Product Attributes`
                        }
                        placeholder={product.material.attributes[attributeKey]}
                        {...register(`material.attributes.${attributeKey}`, {
                          valueAsNumber:
                            productAttributesNames[attributeKey]?.valueAsNumber ?? false,
                        })}
                        {...productAttributesNames[attributeKey]?.endAdornment}
                      />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            );
          }
        )}
        {isUnitprice && (
          <>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Unit Price">
                <div>
                  <CurrencyFormField
                    label="Unit Price"
                    placeholder={(
                      +(product?.material?.unitPriceOverride ?? product?.material?.unitPrice) / 100
                    ).toFixed(2)}
                    disabled={inViewMode}
                    name="material.unitPrice"
                    control={control}
                    {...currencyEndAdornment}
                  />
                </div>
              </Tooltip>
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip
                title={`Production Rate per Day in ${capitalCase(product.material.measurementJob)}`}
              >
                <FormField
                  placeholder={product.material.dailyUnitsComplete}
                  label={`Production Rate per Day in ${capitalCase(
                    product.material.measurementJob
                  )}`}
                  disabled={inViewMode}
                  align="right"
                  {...register("material.dailyUnitsComplete", { valueAsNumber: true })}
                />
              </Tooltip>
            </Grid>
          </>
        )}
        {isSubcontracted && (
          <>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Subcontractor Cost">
                <div>
                  <CurrencyFormField
                    label="Subcontractor Cost"
                    placeholder={(+product.subcontractorCost / 100).toFixed(2)}
                    disabled={inViewMode}
                    align="right"
                    name="subcontractorCost"
                    control={control}
                    {...currencyEndAdornment}
                  />
                </div>
              </Tooltip>
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Subcontractor Name">
                <FormField
                  placeholder={product.subcontractorName}
                  label="Subcontractor Name"
                  disabled={inViewMode}
                  align="right"
                  {...register("subcontractorName")}
                />
              </Tooltip>
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Subcontractor Proposal ID">
                <FormField
                  placeholder={product.subcontractorProposalId}
                  label="Subcontractor Proposal ID"
                  disabled={inViewMode}
                  align="right"
                  {...register("subcontractorProposalId")}
                />
              </Tooltip>
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Overhead Cost Percentage">
                <FormField
                  placeholder={product.material.overheadPercentage}
                  label="Overhead Cost Percentage"
                  disabled={inViewMode}
                  {...register("material.overheadPercentage", { valueAsNumber: true })}
                />
              </Tooltip>
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <Tooltip title="Indirect Cost Percentage">
                <FormField
                  placeholder={product.material.indirectPercentage}
                  label="Indirect Cost Percentage"
                  disabled={inViewMode}
                  align="right"
                  {...register("material.indirectPercentage", { valueAsNumber: true })}
                />
              </Tooltip>
            </Grid>
          </>
        )}
        {!isSubcontracted && !isUnitprice && (
          <>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              {product.material.productionRateType === ProductProductionRateType.TONS_PER_HOUR ? (
                <Tooltip title="Crew Production Rate (Tons per Hour)">
                  <FormField
                    placeholder={product.material.dailyUnitsComplete}
                    label="Crew Production Rate (Tons per Hour)"
                    disabled={
                      inViewMode ||
                      !!getValues("jobDurationOverride") ||
                      !!getValues("jobDurationDaysOverride")
                    }
                    align="right"
                    {...register("material.dailyUnitsComplete", { valueAsNumber: true })}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={`Product Completion Rate per Day in ${capitalCase(
                    product.material.measurementJob
                  )}`}
                >
                  <FormField
                    placeholder={product.material.dailyUnitsComplete}
                    label={`Product Completion Rate per Day in ${capitalCase(
                      product.material.measurementJob
                    )}`}
                    disabled={
                      inViewMode ||
                      !!getValues("jobDurationOverride") ||
                      !!getValues("jobDurationDaysOverride")
                    }
                    align="right"
                    {...register("material.dailyUnitsComplete", { valueAsNumber: true })}
                  />
                </Tooltip>
              )}
            </Grid>
            <Grid className="table-form-field-inline-items" sx={{ minWidth: "300px" }}>
              <ProductCompletionRatePerHour
                averageWorkingHoursPerDay={averageWorkingHoursPerDay}
                loading={organizationSettingsLoading}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Grid item sx={{ marginTop: "10px" }} xs={12}>
        <Grid xs={12} md={12}>
          <Controller
            control={control}
            name="material.statementOfWork"
            render={({ field, fieldState: { error } }) => {
              const handleStatementChange = (content?: string) => {
                setLocalStatementOfWork(content ?? "");
              };
              const handleStatementBlur = (event: any) => {
                const relatedTarget = event?.relatedTarget as HTMLElement | null;
                if (relatedTarget && relatedTarget.closest(".ck-toolbar")) {
                  return;
                }
                if (localStatementOfWork !== field.value) {
                  field.onChange(localStatementOfWork);
                  setValue("material.statementOfWork", localStatementOfWork, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }
                field.onBlur?.();
              };
              return (
                <>
                  <WYSIWYG
                    type="proposalProduct"
                    content={localStatementOfWork}
                    onChange={handleStatementChange}
                    disabled={inViewMode}
                    title="Statement Of Work"
                    plantProductsCount={plantProductsCount}
                    onBlur={handleStatementBlur}
                  />
                  {error && error?.message && (
                    <FormHelperText
                      sx={{
                        color: "red",
                      }}
                    >
                      {error.message}
                    </FormHelperText>
                  )}
                </>
              );
            }}
          />
        </Grid>
      </Grid>
      {!isSubcontracted && (
        <>
          <Grid item xs={12}>
            <hr />
          </Grid>
          <Grid item xs={12}>
            Calculated Inputs
          </Grid>
          <Grid className="table-form-field-inline-content">
            {product.material.type === snakeCase(ProductType.EXCAVATE_OUT) ? (
              <Grid className="table-form-field-inline-items">
                <FormField
                  label={`Material Excavated and Hauled in ${
                    pluralMeasurementMaterial[product.material.measurementMaterial] ?? "Units"
                  }`}
                  placeholder={product.material.materialNeeded}
                  value={materialNeededInput}
                  align="right"
                  onChange={(e) => handleMaterialNeededInputChange(+e.target.value)}
                />
              </Grid>
            ) : (
              <Grid className="table-form-field-inline-items">
                <FormField
                  label={`Material Needed in ${
                    pluralMeasurementMaterial[product.material.measurementMaterial] ?? "Units"
                  }`}
                  placeholder={product.material.materialNeeded}
                  value={materialNeededInput}
                  align="right"
                  onChange={(e) => handleMaterialNeededInputChange(e.target.value)}
                />
              </Grid>
            )}
            <Grid className="table-form-field-inline-items">
              <FormField
                label={`Coverage per ${capitalCase(
                  product.material.measurementMaterial ?? ""
                )} in ${capitalCase(product.material.measurementJob ?? "")}`}
                placeholder={`Coverage per ${capitalCase(
                  product.material.measurementMaterial ?? ""
                )} in ${capitalCase(product.material.measurementJob ?? "")}`}
                value={product.material.areaCoverage?.toFixed(2) || 0}
                align="right"
                disabled
              />
            </Grid>
            <Grid className="table-form-field-inline-items">
              <FormField
                label="Number of Days to Complete Job"
                disabled={inViewMode || !!getValues("jobDurationOverride")}
                align="right"
                placeholder={
                  (product?.jobDurationOverride || product.jobDuration) > 0
                    ? (
                        (product?.jobDurationOverride || product.jobDuration) /
                        averageWorkingHoursPerDay
                      ).toFixed(1)
                    : 0
                }
                {...register("jobDurationDaysOverride", { valueAsNumber: true })}
              />
            </Grid>
            <Grid className="table-form-field-inline-items">
              <FormField
                label="Number of Hours to Complete Job"
                disabled={inViewMode || !!getValues("jobDurationDaysOverride")}
                placeholder={(product?.jobDurationOverride || product?.jobDuration).toFixed(2)}
                align="right"
                {...register("jobDurationOverride", { valueAsNumber: true })}
              />
            </Grid>
            <Grid className="table-form-field-inline-items">
              <FormField
                label="Round Trip Travel Time in Hours"
                disabled={inViewMode}
                placeholder={((stage.distanceSeconds / 3600) * 2).toFixed(2)}
                align="right"
                {...register("roundTripTravelTimeHours", { valueAsNumber: true })}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
}

function ProductCompletionRatePerHour({
  averageWorkingHoursPerDay,
  loading,
}: {
  averageWorkingHoursPerDay: number;
  loading?: boolean;
}) {
  const { product } = useContext(StageProductContext);

  const dailyUnitsComplete =
    product.material.dailyUnitsCompleteOverride ?? product.material.dailyUnitsComplete;
  let productCompletionRatePerHour =
    +dailyUnitsComplete > 0 && averageWorkingHoursPerDay > 0
      ? +dailyUnitsComplete / averageWorkingHoursPerDay
      : 0;
  if (product.material.productionRateType === ProductProductionRateType.TONS_PER_HOUR) {
    productCompletionRatePerHour = +dailyUnitsComplete;
  }

  if (loading) {
    return <FormField label="Product Completion Rate per hour" disabled value="Loading..." />;
  }

  if (!averageWorkingHoursPerDay) {
    return (
      <MDAlert color="warning">
        Please set the Average working hours per day in your organization settings.
      </MDAlert>
    );
  }

  return (
    <Tooltip
      title={`Product Completion Rate Per Hour (Avg ${averageWorkingHoursPerDay}Working Hours Per Day)`}
    >
      <div>
        <FormField
          label={`Product Completion Rate per hour (Avg ${averageWorkingHoursPerDay} Working Hours Per Day)`}
          disabled
          align="right"
          value={productCompletionRatePerHour?.toFixed(2)}
        />
      </div>
    </Tooltip>
  );
}

export default ProposalDefaultsAndInputs;
